import { Box, Flex, Text } from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import useGetMySelf from "@/features/auth/use-get-my-self";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  getIdentity,
  selectCompany,
} from "@/features/ui/helpers/identity-helpers";
import { getDefaultPageByIdentity } from "@/pages";
import { getJwtPayload } from "@/utils/jwt";
import { CompanyType } from "@/features/types";
import AppSelect from "@/features/line-sheet-sets/app-select";
import CenteredSpinner from "@/shared/components/centered-spinner";
import AppNavigationLogo from "@/features/ui/app-navigation/app-navigation-logo";
import { isSupportedLanguage } from "@/features/ui/app-language-selector";
import { resolveTrafficType } from "@/shared/analytics/traffic-type";
import { rememberTrafficType } from "@/shared/analytics/traffic-type-storage";

function stripLocale(pathname: string) {
  const [, first, ...rest] = pathname.split("/");
  if (first && isSupportedLanguage(first)) {
    return `/${rest.join("/")}`;
  }
  return pathname;
}

interface Option {
  id: number;
  type: CompanyType;
  name: string;
}

/**
 * 세션에 선택된 회사가 없을 때, 첫 번째 회사를 자동 선택하는 대신 먼저 띄우는 회사 선택 화면.
 *
 * 라우팅을 하지 않고 현재 화면만 가리므로 사용자가 접속한 주소가 그대로 보존된다.
 * 회사를 고르면 그 주소로 다시 진입한다.
 */
export default function AppCompanySelection() {
  const { data: user, isLoading } = useGetMySelf();
  const { tTitle } = useI18nHelper();

  const scopes = useMemo(() => getJwtPayload()?.scopes ?? [], []);

  const options = useMemo((): AppSelectOption<Option>[] => {
    return scopes.map((scope) => {
      const company = user?.companies?.find(
        (c) => c.id === scope.id && c.type === scope.type
      );

      return {
        // 회사 정보를 아직(혹은 끝내) 못 가져와도 고를 수는 있어야 한다.
        name: company
          ? `[${company.type}] ${company.orgName ?? company.name}`
          : `[${scope.type}] #${scope.id}`,
        value: {
          id: scope.id,
          type: scope.type,
          name: company?.orgName ?? company?.name ?? `#${scope.id}`,
        },
      };
    });
  }, [scopes, user]);

  const handleSelect = useCallback((option?: Option) => {
    if (!option) {
      return;
    }

    // 로그인 직후처럼 "자동 선택된 회사"의 기본 페이지에 우리가 데려다 놓은 경우에만,
    // 고른 회사 기준으로 기본 페이지를 다시 계산한다. 그 외에는 접속한 주소를 그대로 지킨다.
    const isOnAutoSelectedDefaultPage =
      stripLocale(window.location.pathname) ===
      stripLocale(getDefaultPageByIdentity(getIdentity()).split("?")[0]);

    selectCompany(option.id);

    // 아래 replace/reload 로 문서가 다시 뜨므로, 그때 첫 히트가 새 회사 기준으로
    // 찍히도록 traffic_type 을 남긴다.
    rememberTrafficType(resolveTrafficType(getIdentity()));

    if (isOnAutoSelectedDefaultPage) {
      window.location.replace(getDefaultPageByIdentity(getIdentity()));
      return;
    }

    window.location.reload();
  }, []);

  if (isLoading) {
    return <CenteredSpinner />;
  }

  return (
    <Flex
      height={"100vh"}
      background={"#FCFCFE"}
      alignItems={"center"}
      justifyContent={"center"}
    >
      <Box
        width={"320px"}
        padding={"24px"}
        background={"white"}
        borderWidth={"1px"}
        borderColor={"#EFEFED"}
        borderRadius={"4px"}
        boxShadow={"lg"}
      >
        <Flex justifyContent={"center"} marginBottom={"16px"}>
          <AppNavigationLogo collapsed={false} />
        </Flex>
        <Text
          fontSize={"14px"}
          fontWeight={700}
          textAlign={"center"}
          marginBottom={"16px"}
        >
          {tTitle("select_company")}
        </Text>
        <AppSelect<Option>
          name={tTitle("select_company")}
          placeholderText={tTitle("select_company")}
          options={options}
          idFunc={(option) => option.id}
          onSelect={handleSelect}
          isSearchable={true}
          alwaysSearchable={true}
          mobileSheet={true}
          isClearable={false}
          isMultiple={false}
          width={"272px"}
          dropdownWidth={"272px"}
        />
      </Box>
    </Flex>
  );
}
